import {
  CloseOutlined,
  InboxOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";
import { useBriefStore } from "@pages/layout/BriefLayout/lib/useLayoutStore";
import { FirebaseService, ToastService } from "@shared/lib/services";
import { IInvoice, IPayment } from "@shared/lib/types/briefCampains";
import InputNumber from "@shared/ui/InputNumber";
import {
  Button,
  Flex,
  Form,
  Input,
  Select,
  Space,
  Tooltip,
  Upload,
} from "antd";
import { useState } from "react";

const { Option } = Select;
const { Dragger } = Upload;

export function PaymentEntry({
  initialValues = {},
  onSubmit,
  isSubmitting,
  onCancel,
  isView = false,
  invoices,
}: {
  initialValues?: Partial<IPayment>;
  onSubmit?: (values: any, isGst: boolean) => void;
  isSubmitting?: boolean;
  onCancel?: () => void;
  isView?: boolean;
  invoices: IInvoice[];
}) {
  const { briefId } = useBriefStore();
  const [form] = Form.useForm();
  const [fileUrl, setFileUrl] = useState<string | undefined>(
    initialValues?.proofUrl
  );

  const formGstType = Form.useWatch("subType", form);

  const isGstForm = formGstType === "Gst";

  const isTypeGst = initialValues?.subType === "Manual-Gst";

  const handleFileUpload = async (file: File) => {
    try {
      const uploadedUrl = await FirebaseService.uploadFileToFirebase(
        `briefs/${briefId}/payments`,
        file
      );
      setFileUrl(uploadedUrl);

      form.setFieldValue("proofUrl", uploadedUrl);
      ToastService.success("File uploaded successfully");
    } catch (error) {
      ToastService.error("File upload failed. Please try again.");
    }
  };
  const isDisabled = isView || isSubmitting;

  return (
    <Form
      form={form}
      layout="vertical"
      initialValues={{
        campaignInfluencerId: initialValues?.campaignInfluencerId,
        type: initialValues?.type,
        remarks: initialValues?.remarks,
        amount: initialValues?.amount,
        proofUrl: initialValues?.proofUrl,
        linkedInvoiceId: initialValues?.linkedInvoiceId,
        subType: !isTypeGst ? "Base" : "Gst",
      }}
      onFinish={(values) => {
        const { subType, ...valuesWithoutSubType } = values;
        onSubmit?.(valuesWithoutSubType, isGstForm);
      }}
    >
      <Form.Item
        label="Remarks"
        name="remarks"
        rules={[{ required: true, message: "Please enter remarks" }]}
      >
        <Input placeholder="Remarks" disabled={isDisabled} />
      </Form.Item>

      <Form.Item
        label={
          <span>
            Choose Type
            <Tooltip
              title={
                <Flex vertical gap={4}>
                  <p>
                    <strong>Debit:</strong>Used when cost increases e.g.,
                    purchasing an influencer. Money will eventually go to the
                    influencer. Adds to amount payable.
                  </p>
                  <p>
                    <strong>Credit:</strong> Used when cost decreases — e.g.,
                    cancellation or return. Money is recovered or deducted from
                    influencer. Reduces amount payable.
                  </p>
                </Flex>
              }
            >
              <InfoCircleOutlined style={{ marginLeft: 6 }} />
            </Tooltip>
          </span>
        }
        name="type"
        rules={[{ required: true, message: "Please select type" }]}
      >
        <Select placeholder="Debit or Credit?" disabled={isDisabled}>
          <Option value="Debit">
            <Space>
              Debit
              <Tooltip
                title="Used when cost increases e.g.,
                    purchasing an influencer. Money will eventually go to the
                    influencer. Adds to amount payable."
              >
                {" "}
                <InfoCircleOutlined color="#989898" style={{ fontSize: 12 }} />
              </Tooltip>
            </Space>
          </Option>
          <Option value="Credit">
            <Space>
              Credit
              <Tooltip
                title="Used when cost decreases — e.g.,
                    cancellation or return. Money is recovered or deducted from
                    influencer. Reduces amount payable."
              >
                <InfoCircleOutlined color="#989898" style={{ fontSize: 12 }} />
              </Tooltip>
            </Space>
          </Option>
        </Select>
      </Form.Item>
      <Form.Item
        label={<span>Choose Sub Type</span>}
        name="subType"
        rules={[{ required: true, message: "Please select type" }]}
      >
        <Select
          placeholder="Gst or Base?"
          disabled={isDisabled || isTypeGst || Boolean(initialValues?._id)}
          onChange={(value) => {
            // Clear linkedInvoiceId when subType changes away from GST
            if (value !== "Gst") {
              form.setFieldValue("linkedInvoiceId", undefined);
            }
          }}
        >
          <Option value="Gst">
            <Space>Gst</Space>
          </Option>
          <Option value="Base">
            <Space>Base</Space>
          </Option>
        </Select>
      </Form.Item>
      {isGstForm && (
        <Form.Item
          label="Linked Invoice"
          name="linkedInvoiceId"
          rules={[
            {
              required: true,
              message: "Please select a linked invoice",
            },
          ]}
        >
          <Select placeholder="Select Linked Invoice" disabled={isDisabled}>
            {invoices.map((invoice) => (
              <Option key={invoice._id} value={invoice._id}>
                {invoice.remarks}
              </Option>
            ))}
          </Select>
        </Form.Item>
      )}

      <Form.Item
        label="Total amount paid"
        name="amount"
        rules={[{ required: true, message: "Please enter amount" }]}
      >
        <InputNumber
          type="number"
          placeholder="Enter Amount"
          disabled={isDisabled}
        />
      </Form.Item>

      <Form.Item label="Payment Proof" name="proofUrl">
        <Dragger
          name="file"
          multiple={false}
          customRequest={async ({ file, onSuccess, onError }) => {
            try {
              await handleFileUpload(file as File);
            } catch (err) {
              onError && onError(err as Error);
            }
          }}
          showUploadList={false}
          disabled={isSubmitting}
        >
          <p className="ant-upload-drag-icon">
            <InboxOutlined style={{ color: "#1890ff" }} />
          </p>
          <p className="ant-upload-text">
            Click or drag file to this area to upload
          </p>
          <p className="ant-upload-hint">Upload Invoice here</p>
        </Dragger>
        {fileUrl && (
          <div style={{ marginTop: 8 }}>
            <a
              href={fileUrl}
              target="_blank"
              rel="noopener noreferrer"
              style={{ width: "80" }}
            >
              file
            </a>
            <CloseOutlined
              style={{ marginLeft: 8 }}
              onClick={() => {
                setFileUrl(undefined);
                form.setFieldValue("proofUrl", undefined);
              }}
            />
          </div>
        )}
      </Form.Item>

      <Form.Item>
        <Space style={{ width: "100%", justifyContent: "flex-end" }}>
          <Button onClick={onCancel} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            type="primary"
            htmlType="submit"
            disabled={isDisabled}
            loading={isSubmitting}
          >
            Save
          </Button>
        </Space>
      </Form.Item>
    </Form>
  );
}
